/**
 * Master email controls — administrator-only switches read by the backend
 * before any automated email is sent.
 *
 * Rows live in `public.email_controls`; every change is audited automatically
 * by a database trigger into `public.email_control_audit`.
 */

import { supabase } from "@/integrations/supabase/client";

export type EmailControlKey =
  | "parent_confirmation"
  | "payment_receipt"
  | "welcome_packet"
  | "admin_notice"
  | "instructor_notice"
  | "reminder_emails";

export interface EmailControlRow {
  control_key: EmailControlKey;
  label: string;
  description: string | null;
  enabled: boolean;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface EmailControlAuditRow {
  id: string;
  control_key: string;
  previous_enabled: boolean | null;
  new_enabled: boolean;
  changed_by_email: string | null;
  created_at: string;
}

/** Display order in Settings → Communications. */
export const EMAIL_CONTROL_ORDER: EmailControlKey[] = [
  "parent_confirmation",
  "payment_receipt",
  "welcome_packet",
  "admin_notice",
  "instructor_notice",
  "reminder_emails",
];

export async function loadEmailControls(): Promise<EmailControlRow[]> {
  const { data, error } = await (supabase as any)
    .from("email_controls")
    .select("control_key, label, description, enabled, updated_at, updated_by_email");
  if (error) throw error;
  const rows = (data ?? []) as EmailControlRow[];
  return rows.sort(
    (a, b) =>
      EMAIL_CONTROL_ORDER.indexOf(a.control_key) - EMAIL_CONTROL_ORDER.indexOf(b.control_key),
  );
}

export async function setEmailControl(key: EmailControlKey, enabled: boolean): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await (supabase as any)
    .from("email_controls")
    .update({
      enabled,
      updated_by: auth?.user?.id ?? null,
      updated_by_email: auth?.user?.email ?? null,
    })
    .eq("control_key", key);
  if (error) throw error;
}

export async function loadEmailControlAudit(limit = 25): Promise<EmailControlAuditRow[]> {
  const { data, error } = await (supabase as any)
    .from("email_control_audit")
    .select("id, control_key, previous_enabled, new_enabled, changed_by_email, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as EmailControlAuditRow[];
}

export interface EmailSkipNotice {
  id: string;
  template_name: string;
  reason: string;
  recipient_email: string | null;
  class_name: string | null;
  class_slug: string | null;
  session_label: string | null;
  related_item_id: string | null;
  acknowledged: boolean;
  created_at: string;
}

export async function loadSkipNotices(limit = 20): Promise<EmailSkipNotice[]> {
  const { data, error } = await (supabase as any)
    .from("email_skip_notices")
    .select("*")
    .eq("acknowledged", false)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as EmailSkipNotice[];
}

export async function acknowledgeSkipNotice(id: string): Promise<void> {
  const { error } = await (supabase as any)
    .from("email_skip_notices")
    .update({ acknowledged: true })
    .eq("id", id);
  if (error) throw error;
}

/**
 * Human wording for a delivery's real state. The internal queue only proves a
 * message was accepted into the queue — never call that "Sent".
 */
export function emailStatusLabel(status: string): string {
  switch (status) {
    case "pending":
    case "queued":
      return "Queued";
    case "sent":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "failed":
    case "dlq":
    case "bounced":
      return "Failed";
    case "suppressed":
      return "Suppressed";
    case "expired_due_to_sender_setup":
      return "Failed (sender domain not verified)";
    default:
      return status;
  }
}

/** Turn a raw provider error into something a person can act on. */
export function emailFailureReason(raw?: string | null): string {
  const text = String(raw || "").trim();
  if (!text) return "No reason reported by the email provider.";
  if (text.includes("no_matching_sender")) {
    return "Rejected by the email provider: no verified sender domain is configured for this project.";
  }
  if (text.includes("suppress")) {
    return "Blocked: this address is on the suppression list (previous bounce, complaint, or unsubscribe).";
  }
  return text;
}

export interface FailedEmail {
  id: string;
  message_id: string;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

/** Deliveries that failed or dead-lettered — resendable by an administrator. */
export async function loadFailedEmails(limit = 20): Promise<FailedEmail[]> {
  const { data, error } = await (supabase as any)
    .from("email_send_log")
    .select("id, message_id, template_name, recipient_email, status, error_message, created_at")
    .in("status", ["failed", "dlq"])
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as FailedEmail[];
}

export async function resendFailedEmail(messageId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("admin-resend-email", {
    body: { messageId },
  });
  if (error) throw error;
  if ((data as any)?.error) throw new Error(String((data as any).error));
}
